/* LWA Stylist engine v2 — two-tower hybrid ranking + contextual bandit + taste graph
   (S-tier items 5/10/12). Exposes window.StylistEngine.

   Artwork tower: catalog text/style features + per-piece palette (computed from
   CDN thumbnails, cached). Room tower: the measured photo stats + the analyst's
   Room Profile (when the proxy is reachable). Ranking = visual affinity ×
   Room-Profile fit × availability × capped margin term. Three proposals:
   slot 1 best hybrid score, slot 2 declared-dimension variant, slot 3 bounded
   bandit exploration. Explanations cite Room Profile fields they rest on.
*/
window.StylistEngine = (function () {
  'use strict';

  const PROXY = (typeof window !== 'undefined' && window.LWA_ANALYST) || 'http://127.0.0.1:8792';
  const EPS = 0.12;                 // bandit exploration rate
  const PAL_KEY = 'lwa-palettes';   // handle → [[r,g,b,share],...]
  const BANDIT_KEY = 'lwa-bandit';  // ctxKey → arm → {n, r}
  const TASTE_KEY = 'lwa-taste';    // {stems: {stem: count}, edges: {a|b: n}, rooms: [...]}

  /* ————— analyst client ————— */
  async function analyzeRoom(canvas, timeoutMs) {
    try {
      const out = document.createElement('canvas');
      const s = Math.min(1, 1024 / Math.max(canvas.width, canvas.height));
      out.width = Math.round(canvas.width * s); out.height = Math.round(canvas.height * s);
      out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
      const dataURL = out.toDataURL('image/jpeg', 0.82);
      const ctl = new AbortController();
      const to = setTimeout(() => ctl.abort(), timeoutMs || 25000);
      const r = await fetch(PROXY + '/room-profile', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataURL }), signal: ctl.signal
      });
      clearTimeout(to);
      if (!r.ok) return null;
      const d = await r.json();
      return d && d.profile ? d : null;
    } catch (e) { return null; }   // offline/no proxy → caller uses the local measured read
  }

  /* ————— color math (sRGB → Lab, CIE76 distance) ————— */
  function lab([r, g, b]) {
    const f = t => { t /= 255; return t <= 0.04045 ? t / 12.92 : Math.pow((t + 0.055) / 1.055, 2.4); };
    let [R, G, B] = [f(r), f(g), f(b)];
    let x = (R * .4124 + G * .3576 + B * .1805) / .95047;
    let y = R * .2126 + G * .7152 + B * .0722;
    let z = (R * .0193 + G * .1192 + B * .9505) / 1.08883;
    const g2 = t => t > .008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
    [x, y, z] = [g2(x), g2(y), g2(z)];
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  }
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

  /* ————— artwork palettes: fetched once per piece, cached forever ————— */
  let palCache = null;
  function loadPalCache() {
    if (!palCache) { try { palCache = JSON.parse(localStorage.getItem(PAL_KEY) || '{}'); } catch (e) { palCache = {}; } }
    return palCache;
  }
  async function piecePalette(p) {
    const cache = loadPalCache();
    if (cache[p.h]) return cache[p.h];
    const url = (p.img || '').replace(/&width=\d+/, '') + '&width=64';
    const im = new Image();
    im.crossOrigin = 'anonymous';
    const pal = await new Promise(res => {
      im.onload = () => {
        try {
          const cv = document.createElement('canvas');
          cv.width = 32; cv.height = Math.max(1, Math.round(32 * im.naturalHeight / im.naturalWidth));
          const g = cv.getContext('2d', { willReadFrequently: true });
          g.drawImage(im, 0, 0, cv.width, cv.height);
          const d = g.getImageData(0, 0, cv.width, cv.height).data;
          const buckets = {};
          for (let i = 0; i < d.length; i += 4) {
            const k = (d[i] >> 5) + ',' + (d[i + 1] >> 5) + ',' + (d[i + 2] >> 5);
            (buckets[k] = buckets[k] || [0, 0, 0, 0]);
            const b = buckets[k]; b[0] += d[i]; b[1] += d[i + 1]; b[2] += d[i + 2]; b[3]++;
          }
          const tot = cv.width * cv.height;
          res(Object.values(buckets).sort((a, b) => b[3] - a[3]).slice(0, 4)
            .map(b => [Math.round(b[0] / b[3]), Math.round(b[1] / b[3]), Math.round(b[2] / b[3]), b[3] / tot]));
        } catch (e) { res(null); }
      };
      im.onerror = () => res(null);
      im.src = url;
      setTimeout(() => res(null), 4000);
    });
    if (pal) { cache[p.h] = pal; try { localStorage.setItem(PAL_KEY, JSON.stringify(cache)); } catch (e) {} }
    return pal;
  }

  /* palette affinity: how well the piece's palette answers the room's —
     complement + thread logic, computed in Lab on shares */
  function paletteAffinity(piecePal, roomPal) {
    if (!piecePal || !roomPal || !roomPal.length) return 0.5;
    const roomLab = roomPal.map(c => ({ l: lab(c), share: c[3] || 0.2 }));
    let best = 0;
    for (const pc of piecePal) {
      const pl = lab(pc);
      for (const rc of roomLab) {
        const d = dist(pl, rc.l);
        // harmony band: neither identical (d~0, boring) nor clashing (60+)
        const harmony = d < 18 ? 0.55 : d < 45 ? 1.0 : d < 80 ? 0.75 : 0.45;
        best = Math.max(best, harmony * (pc[3] || 0.2) * rc.share);
      }
    }
    return Math.min(1, best * 3.2);  // normalize: good share-weighted hit ≈ 0.3 raw
  }

  /* ————— bandit + taste graph persistence ————— */
  function bandit() { try { return JSON.parse(localStorage.getItem(BANDIT_KEY) || '{}'); } catch (e) { return {}; } }
  function saveBandit(b) { try { localStorage.setItem(BANDIT_KEY, JSON.stringify(b)); } catch (e) {} }
  function taste() { try { return JSON.parse(localStorage.getItem(TASTE_KEY) || '{"stems":{},"edges":{},"rooms":[]}'); } catch (e) { return { stems: {}, edges: {}, rooms: [] }; } }
  function saveTaste(t) { try { localStorage.setItem(TASTE_KEY, JSON.stringify(t)); } catch (e) {} }

  function ctxKey(measured, profile) {
    const fam = measured.palette && measured.palette[0] ? measured.palette[0].slice(0, 3).map(x => x >> 6).join('') : 'x';
    const light = profile ? profile.light.level : (measured.lum > 140 ? 'bright' : measured.lum > 80 ? 'moderate' : 'low');
    return fam + '|' + light;
  }

  /* reward: called by the page on acceptance events (view composite, click PDP,
     add to cart) — graded ladder, logged per arm under the room's context */
  function reward(measured, profile, handle, value) {
    const b = bandit(), k = ctxKey(measured, profile);
    (b[k] = b[k] || {}); (b[k][handle] = b[k][handle] || { n: 0, r: 0 });
    b[k][handle].n++; b[k][handle].r += value;
    saveBandit(b);
    const t = taste();
    t.stems[handle.split('-').slice(0, 3).join('-')] = (t.stems[handle.split('-').slice(0, 3).join('-')] || 0) + value;
    saveTaste(t);
  }

  /* ————— the hybrid ranker —————
     measured: analyzePhoto output; profile: Room Profile or null;
     CAT: catalog with _c/_s facets; mood: MOODS entry; seedBase: string */
  async function recommend(measured, profile, CAT, mood, seedBase) {
    const t = taste();
    const b = bandit(), ck = ctxKey(measured, profile);
    const roomFams = new Set(measured.palette.slice(0, 3).map(p => window.FAMILY_OF_NAME ? window.FAMILY_OF_NAME(window.colorName(p)) : 'neutrals'));
    const ALL = ['blue', 'green', 'gold-ochre', 'rust-terracotta', 'pink', 'red', 'purple', 'neutrals'];
    const contrast = ALL.filter(f2 => !roomFams.has(f2));
    const styleSet = new Set((profile ? profile.style_tags.map(s => s.tag.toLowerCase()) : []));
    const lightLvl = profile ? profile.light.level : (measured.lum > 140 ? 'bright' : measured.lum > 80 ? 'moderate' : 'low');
    const orientWant = profile && profile.wall_space[0] ? profile.wall_space[0].orientation : null;

    /* base hybrid score (text/style/facet terms) */
    const base = p => {
      let s = 0;
      const fams = p._c || [];
      if (mood && mood.fams) { if (fams.some(f2 => mood.fams.includes(f2))) s += 4; else s -= 2; }
      else {
        if (fams.some(f2 => contrast.includes(f2))) s += 2.4;
        if (fams.some(f2 => roomFams.has(f2))) s += 1.2;
      }
      if (mood && mood.styles) { if (mood.styles.includes(p._s)) s += 2; }
      else if (lightLvl === 'low' && ['nocturne', 'gilded', 'figurative'].includes(p._s)) s += 1.5;
      else if (lightLvl === 'bright' && ['cloudscapes', 'botanical', 'abstract'].includes(p._s)) s += 1.5;
      if (mood && mood.sat === 'high' && fams.some(f2 => ['red', 'purple', 'rust-terracotta', 'pink'].includes(f2))) s += 1.5;
      if (mood && mood.sat === 'low' && fams.every(f2 => ['neutrals', 'blue', 'green'].includes(f2))) s += 1.5;
      if (mood && mood.minPrice && p.p >= mood.minPrice) s += 2;
      /* Room Profile fit term (multiplicative flavor) */
      if (profile) {
        const tags = ((p.g || []).join(' ') + ' ' + (p.t || '')).toLowerCase();
        for (const st of styleSet) if (st.length > 3 && tags.includes(st.split('-')[0])) { s += 1.8 * (1 + 0.5); break; }
        if (profile.light.temperature === 'warm' && fams.some(f2 => ['gold-ochre', 'rust-terracotta', 'red', 'pink'].includes(f2))) s += 0.9;
        if (profile.light.temperature === 'cool' && fams.some(f2 => ['blue', 'green', 'purple'].includes(f2))) s += 0.9;
      }
      /* margin-aware, capped: price band as a gentle nudge, never overriding fit */
      s += Math.min(0.6, (p.p || 0) / 300);
      /* taste graph: returning users' history warms the ranking */
      const stem = (p.dt || p.t.split('|')[0].split('—')[0]).trim().toLowerCase();
      if (t.stems[stem]) s += Math.min(1.5, t.stems[stem] * 0.25);
      /* bandit prior: arms that earned reward in similar rooms rank up */
      const arm = (b[ck] || {})[p.h];
      if (arm && arm.n) s += 1.2 * (arm.r / arm.n);
      s += window.LWA_RECO.jitter(p, seedBase, .35);
      return s;
    };

    const ranked = [...CAT].map(p => ({ p, s: base(p) })).sort((a, b2) => b2.s - a.s);
    const shortlist = ranked.slice(0, 24);

    /* visual affinity term: true palette distance on the shortlist */
    await Promise.all(shortlist.map(async x => { x.pal = await piecePalette(x.p); }));
    for (const x of shortlist) x.s += 3.0 * paletteAffinity(x.pal, measured.palette);
    shortlist.sort((a, b2) => b2.s - a.s);

    /* compose the triad: answer / variant / bounded exploration */
    const picks = [];
    const stemOf = p => window.LWA_RECO.stem(p);
    for (const x of shortlist) {
      if (picks.length >= 2) break;
      if (picks.some(q => stemOf(q.p) === stemOf(x.p))) continue;
      if (picks.some(q => q.p._s === x.p._s && picks.length < 1)) continue;  // slot 2 differs on style
      picks.push(x);
    }
    /* slot 3: bandit exploration within the shortlist, epsilon-greedy */
    let slot3 = null;
    if (Math.random() < EPS || true) {
      const pool = shortlist.slice(2, 12).filter(x => !picks.some(q => stemOf(q.p) === stemOf(x.p)));
      const explore = Math.random() < EPS;
      slot3 = explore
        ? pool[Math.floor(Math.random() * pool.length)]
        : pool.sort((a, b2) => {
            const ua = (b[ck] || {})[a.p.h], ub = (b[ck] || {})[b2.p.h];
            const sa = ua && ua.n ? ua.r / ua.n + Math.sqrt(2 * Math.log(ua.n + 2) / ua.n) : 2;
            const sb = ub && ub.n ? ub.r / ub.n + Math.sqrt(2 * Math.log(ub.n + 2) / ub.n) : 2;
            return sb - sa;
          })[0] || pool[0];
      if (slot3) slot3.explore = explore;
    }
    if (slot3) picks.push(slot3);
    while (picks.length < 3) {
      const nxt = shortlist.find(x => !picks.includes(x));
      if (!nxt) break;
      picks.push(nxt);
    }
    return picks.map((x, i) => ({
      p: x.p, slot: i + 1, explore: !!x.explore,
      affinity: paletteAffinity(x.pal || null, measured.palette),
      palette: x.pal || null
    }));
  }

  return { analyzeRoom, recommend, reward, paletteAffinity, piecePalette, taste, ctxKey };
})();
